import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, Pencil } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

interface BlogGroup {
  id?: string;
  name: string;
  description: string | null;
  sort_order: number;
}

interface BlogGroupsTabProps {
  blogGroups: BlogGroup[];
  setBlogGroups: React.Dispatch<React.SetStateAction<BlogGroup[]>>;
}

const BlogGroupsTab: React.FC<BlogGroupsTabProps> = ({ blogGroups, setBlogGroups }) => {
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isGroupEditMode, setIsGroupEditMode] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<BlogGroup | null>(null);
  const [groupFormData, setGroupFormData] = useState<BlogGroup>({
    name: '',
    description: null,
    sort_order: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBlogGroups();
  }, []);

  const fetchBlogGroups = async () => {
    try {
      
      const response = await axios.get('/api/blog-groups');
      
      setBlogGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching blog groups:', error);
      setBlogGroups([]);
      toast({
        title: "Error",
        description: "Failed to load blog groups",
        variant: "destructive"
      });
    }
  };

  const handleGroupInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddGroup = () => {
    setIsGroupEditMode(false);
    setSelectedGroup(null);
    setGroupFormData({
      name: '',
      description: null,
      sort_order: 0
    });
    setIsGroupDialogOpen(true);
  };

  const handleEditGroup = (group: BlogGroup) => {
    setIsGroupEditMode(true);
    setSelectedGroup(group);
    setGroupFormData({
      name: group.name || '',
      description: group.description || null,
      sort_order: group.sort_order || 0
    });
    setIsGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    try {
      await axios.delete(`/api/blog-groups/${id}`);
      toast({
        title: "Success",
        description: "Blog group deleted successfully",
      });
      fetchBlogGroups();
    } catch (error) {
      console.error('Error deleting blog group:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog group",
        variant: "destructive"
      });
    }
  };

  const handleSaveGroup = async () => {
    try {
      if (!groupFormData.name.trim()) {
        toast({
          title: "Validation Error",
          description: "Group name is required.",
          variant: "destructive",
        });
        return;
      }
      setIsSubmitting(true);
      
      const dataToSend = {
        ...groupFormData,
        sort_order: Number(groupFormData.sort_order) || 0
      };
      let response;
      if (isGroupEditMode && selectedGroup?.id) {
        response = await axios.put(`/api/blog-groups/${selectedGroup.id}`, dataToSend);
        
        toast({
          title: "Success",
          description: "Blog group updated successfully",
        });
      } else {
        response = await axios.post('/api/blog-groups', dataToSend);
        
        toast({
          title: "Success",
          description: "Blog group created successfully",
        });
      }
      setIsGroupDialogOpen(false);
      await fetchBlogGroups();
    } catch (error) {
      console.error('Error saving blog group:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save blog group",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Group Management</h1>
        <Button onClick={handleAddGroup}>
          <PlusCircle className="mr-2 h-4 w-4" />
          New Group
        </Button>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">#</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Sort Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {blogGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">No blog groups found</TableCell>
              </TableRow>
            ) : (
              blogGroups.map((group, index) => (
                <TableRow key={group.id}>
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.description || '-'}</TableCell>
                  <TableCell>{group.sort_order}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditGroup(group)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeleteGroup(group.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="group-dialog-description">
          <DialogHeader>
            <DialogTitle>{isGroupEditMode ? 'Edit Blog Group' : 'Add New Blog Group'}</DialogTitle>
            <DialogDescription id="group-dialog-description">
              Fill in the details for the blog group below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">Name</label>
              <Input
                id="name"
                name="name"
                value={groupFormData.name}
                onChange={handleGroupInputChange}
                placeholder="Enter group name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">Description</label>
              <Textarea
                id="description"
                name="description"
                value={groupFormData.description || ''}
                onChange={handleGroupInputChange}
                placeholder="Enter group description"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="sort_order" className="text-sm font-medium">Sort Order</label>
              <Input
                id="sort_order"
                name="sort_order"
                type="number"
                value={groupFormData.sort_order}
                onChange={handleGroupInputChange}
                placeholder="Enter sort order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGroupDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup} disabled={isSubmitting}>
              {isGroupEditMode ? (
                isSubmitting ? 'Updating...' : 'Update'
              ) : (
                isSubmitting ? 'Creating...' : 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogGroupsTab;